import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import injectStyles from 'react-jss'
import { useHistory } from 'react-router-dom'

import { ClientAddWrapper } from 'Components/Clients/ClientAdd/ClientAddWrapper'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { createClient, updateClientById } from 'Reducers/Clients/actions'
import { uploadClientImage } from 'Reducers/Upload/actions'
import ClientsIcon from 'Assets/Images/icons/clients'
import { PATHS } from 'Config/Routing/paths'
import ClientForm from '../ClientForm'
import { CLIENT_FORM_MODES } from '../const'
import { getTagsGQL } from './gql'
import { apiXero } from '../../../Module/API/Xero'
import { getFormattedContact } from '../XeroClient/utils'
import LoadingIndicator from '../../../Components/LoadingIndicator/LoadingIndicator'
import { isProduction } from '../../../Utils/Functions'
import { styles } from './styles'
import { showReduxModal } from '../../../Reducers/UI/actions'
import StyledBreadcrumbs from '../../../Components/StyledBreadcrumbs'

const BreadcrumbsClientAdd = () => (
  <StyledBreadcrumbs
    icon={<ClientsIcon style={{ width: 24 }} />}
    breadcrumbs={[
      {
        label: 'Clients',
        link: `${PATHS.main.dashboard}${PATHS.dashboard.clients}`,
      },
      { label: 'Add' },
    ]}
  />
)

const ClientAdd = ({ countries, classes }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [allTags, setAllTags] = useState([])
  const [tenant, setTenant] = useState(null)
  const [isFetchedData, setIsFetchedData] = useState(false)
  const [xeroInfo, setXeroInfo] = useState({
    isLoading: true,
  })
  const [usersHashMap, setUsersHashMap] = useState({})
  const [name, setName] = useState('')
  const [isFetchingSimilarContact, setIsFetchingSimilarContact] = useState(
    false,
  )

  const fetchTags = async () => {
    const response = await getTagsGQL()
    const { data } = await response.json()

    const result = data.getTags.edges.map(item => item.node)
    setAllTags(result)
  }

  const fetchTenant = async () => {
    const result = await apiXero.getXeroTenant({
      includeAvailableAccount: true,
      includeCurrencies: true,
    })
    if (result && result.isAuthorized && result.isConnected) {
      setTenant(result)
      setXeroInfo({
        isLoading: false,
        entity: result.name,
      })
    } else {
      setXeroInfo({
        isLoading: false,
        entity: null,
      })
    }
  }

  const fetchAllData = async () => {
    setLoading(true)
    await fetchTags()
    try {
      await fetchTenant()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log({ err })
      setXeroInfo({
        isLoading: false,
        error: 'Something went wrong',
      })
    } finally {
      setLoading(false)
      setIsFetchedData(true)
    }
  }

  useEffect(() => {
    if (!isFetchedData) {
      fetchAllData()
    }
  }, [])

  const navigateBack = () => {
    history.push(`${PATHS.main.dashboard}${PATHS.dashboard.clients}`)
  }

  const callbackNavigateBack = {
    onSuccess: navigateBack,
  }

  const handleUpdateLogoAndNavigateBack = ({ id }, logo) => {
    const callbackUpdateClient = {
      onSuccess: imageUrl => {
        dispatch(updateClientById(callbackNavigateBack, id, { logo: imageUrl }))
      },
    }
    dispatch(uploadClientImage(id, logo, callbackUpdateClient))
  }

  const createXeroContact = async ({ xeroValues, client }) => {
    const formattedXeroValues = Object.values(xeroValues).reduce(
      (acc, item) => {
        const withoutXero = item.name.replace('xero', '')
        const newKey = `${withoutXero
          .charAt(0)
          .toLowerCase()}${withoutXero.slice(1)}`
        acc[newKey] = item.value
        return acc
      },
      {},
    )
    const currentCounty = countries.find(
      country => country.id == client.countryId,
    )

    const contact = getFormattedContact(
      {
        ...formattedXeroValues,
        country: currentCounty.name,
        trackingCategoryName: client.name,
      },
      {
        usersHashMap,
        clientId: client.id,
      },
    )

    await apiXero.createXeroContact({
      contact,
      clientName: client.name,
      tenantId: tenant.tenantId,
    })
  }

  const handleClientAddFormSubmit = async (
    {
      name,
      code,
      tags,
      country,
      industry,
      industryGroup,
      isSkipCheckoutEnabled,
      addressVerificationEnabled,
      addressVerificationDailyBudget,
      addressVerificationMonthlyBudget,
      ...xeroValues
    },
    isValid,
    logo,
  ) => {
    if (isValid) {
      setLoading(true)
      const payload = {
        name: name.value,
        countryId: country.value,
      }

      if (industry && industry.value) {
        payload.industry = industry.value
      }
      if (industryGroup && industryGroup.value) {
        payload.industryGroup = industryGroup.value
      }
      if (isSkipCheckoutEnabled) {
        payload.isSkipCheckoutEnabled = Boolean(isSkipCheckoutEnabled.value)
      }

      if (addressVerificationEnabled && country.value === 14) {
        payload.addressVerificationEnabled = Boolean(
          addressVerificationEnabled.value,
        )

        // Default to $50 daily and $200 monthly if not provided
        if (
          addressVerificationDailyBudget &&
          addressVerificationDailyBudget.value !== undefined &&
          addressVerificationDailyBudget.value !== ''
        ) {
          payload.addressVerificationDailyBudget = Math.round(
            parseFloat(addressVerificationDailyBudget.value) * 100,
          )
        } else {
          payload.addressVerificationDailyBudget = 5000
        }

        if (
          addressVerificationMonthlyBudget &&
          addressVerificationMonthlyBudget.value !== undefined &&
          addressVerificationMonthlyBudget.value !== ''
        ) {
          payload.addressVerificationMonthlyBudget = Math.round(
            parseFloat(addressVerificationMonthlyBudget.value) * 100,
          )
        } else {
          payload.addressVerificationMonthlyBudget = 20000
        }
      }

      const callbackCreateXeroAndNavigateBack = {
        onSuccess: client => {
          if (tenant && isProduction()) {
            createXeroContact({ xeroValues, client })
          }
          navigateBack()
        },
      }

      if (logo) {
        const callbackUpdateLogo = {
          onSuccess: res => {
            handleUpdateLogoAndNavigateBack(res, logo)
          },
        }
        dispatch(createClient(callbackUpdateLogo, payload))
      } else {
        dispatch(createClient(callbackCreateXeroAndNavigateBack, payload))
      }
      setLoading(false)
    }
  }

  const onBlurInputName = useCallback(async () => {
    if (name && name.length > 2) {
      if (tenant && tenant.tenantId) {
        setIsFetchingSimilarContact(true)
        const result = await apiXero.checkOnSimilarContact({
          tenantId: tenant.tenantId,
          contactName: name,
        })
        setIsFetchingSimilarContact(false)
        if (result.isExist) {
          dispatch(
            showReduxModal({
              title: `Please ensure that this client does NOT already exist in Xero`,
              children: (
                <div style={{ textAlign: 'center' }}>
                  You try to create client with name - {name}
                  <br />
                  But we have similar contact in Xero - {result.closestContact}
                </div>
              ),
            }),
          )
        }
      }
    }
  }, [name && name.length])

  return (
    <LayoutDashboard>
      <ClientAddWrapper>
        <BreadcrumbsClientAdd />
        {isFetchingSimilarContact ? (
          <div className={classes.loadingOverlay}>Loading...</div>
        ) : null}
        {loading ? (
          <div className={classes.loadingWrapper}>
            <LoadingIndicator />
          </div>
        ) : null}
        {allTags && !loading && (
          <ClientForm
            countries={countries}
            handleSubmit={handleClientAddFormSubmit}
            mode={CLIENT_FORM_MODES.ADD}
            buttonsDisabled={loading}
            handleCancel={navigateBack}
            tags={allTags}
            xeroInfo={xeroInfo}
            usersHashMap={usersHashMap}
            setUsersHashMap={setUsersHashMap}
            tenant={tenant}
            onBlurInputName={onBlurInputName}
            setName={setName}
          />
        )}
      </ClientAddWrapper>
    </LayoutDashboard>
  )
}

export default injectStyles(styles)(ClientAdd)
